import type { LucideIcon } from 'lucide-react';
import { PlugZap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { MetricCard } from '@/components/financials/MetricCard';
import { cn } from '@/lib/utils';
import type { MarketingTile } from '@/features/marketing/tiles';

interface MetricTileProps {
  tile: MarketingTile;
  loading?: boolean;
  /** Label for the comparison period, e.g. "vs prior 28 days". */
  deltaLabel?: string;
  className?: string;
}

/**
 * Renders one tile of the Marketing Dashboard overview.
 *
 * Wraps the shared `MetricCard` but adds the state that matters on this
 * surface: a tile whose provider hasn't reported yet renders as an explicit
 * "not connected" placeholder rather than a zero. A zero on a revenue or
 * follower tile is indistinguishable from real bad news, and this dashboard
 * drives a bi-weekly review — so the difference between "no data" and
 * "no results" has to be visible at a glance.
 */
export function MetricTile({ tile, loading, deltaLabel, className }: MetricTileProps) {
  const Icon = tile.icon;

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {tile.label}
          </CardTitle>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (!tile.available) {
    return (
      <Card className={cn('border-dashed bg-card/50', className)}>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {tile.label}
          </CardTitle>
          <Icon className="h-4 w-4 text-muted-foreground/60" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-medium tracking-headline text-muted-foreground/60">—</div>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
            <PlugZap className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={1.8} />
            <span>Waiting on {tile.requires}</span>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <MetricCard
      className={className}
      label={tile.label}
      value={tile.value}
      delta={tile.delta}
      deltaLabel={deltaLabel}
      hint={tile.hint}
      icon={<Icon className="h-4 w-4 text-muted-foreground" />}
    />
  );
}

interface StatTileProps {
  label: string;
  value: string;
  icon?: LucideIcon;
  delta?: number | null;
  deltaLabel?: string;
  hint?: string;
  loading?: boolean;
}

/**
 * Lighter-weight sibling of `MetricTile` for the per-channel tabs, where the
 * surrounding section already tells the operator which integration is in
 * play — so there's no per-tile "not connected" state to render.
 */
export function StatTile({
  label,
  value,
  icon: Icon,
  delta,
  deltaLabel,
  hint,
  loading,
}: StatTileProps) {
  return (
    <MetricCard
      label={label}
      value={value}
      delta={delta}
      deltaLabel={deltaLabel}
      hint={hint}
      loading={loading}
      icon={Icon ? <Icon className="h-4 w-4 text-muted-foreground" /> : undefined}
    />
  );
}
